import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { employeeApi, getEmployeeToken } from '../../lib/employeeApi'
import MobileLayout from '../../components/MobileLayout'
import { FileText, ChevronLeft, ExternalLink } from 'lucide-react'
import { Capacitor } from '@capacitor/core'

interface JobSheet {
  id: string
  title: string
  description?: string
  fileName: string
  fileSize: number
  positionName?: string
  createdAt: string
}

const API_BASE = (import.meta as any).env?.VITE_API_URL || ''

export default function MobileJobSheets() {
  const navigate = useNavigate()
  const [sheets, setSheets] = useState<JobSheet[]>([])
  const [loading, setLoading] = useState(true)
  const [opening, setOpening] = useState<string | null>(null)

  useEffect(() => {
    employeeApi.get('/employee/me/job-sheets')
      .then(r => setSheets(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const openPdf = async (sheet: JobSheet) => {
    setOpening(sheet.id)
    try {
      const token = getEmployeeToken()
      const url = `${API_BASE}/api/employee/me/job-sheets/${sheet.id}?token=${token}`
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import('@capacitor/browser')
        await Browser.open({ url })
      } else {
        window.open(url, '_blank')
      }
    } catch { alert('Impossible d\'ouvrir le PDF') }
    finally { setOpening(null) }
  }

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
        <button onClick={() => navigate('/app/home')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 flex items-center gap-2">
            <FileText size={18} className="text-blue-600" /> Fiches de poste
          </h1>
          <p className="text-xs text-gray-500">{sheets.length} fiche(s) disponible(s)</p>
        </div>
      </div>

      <div className="px-4 py-4">
        {loading ? (
          <div className="text-center py-16 text-gray-400">Chargement...</div>
        ) : sheets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <FileText size={48} className="text-gray-200" />
            <p className="text-gray-400 text-sm">Aucune fiche disponible pour le moment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sheets.map(s => (
              <button key={s.id} onClick={() => openPdf(s)}
                disabled={opening === s.id}
                className="w-full bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-4 text-left hover:border-blue-300 active:scale-98 transition-all disabled:opacity-60">
                {/* Icône PDF */}
                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText size={26} className="text-red-500" />
                </div>
                {/* Infos */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm leading-tight">{s.title}</p>
                  {s.description && (
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{s.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-xs text-gray-400">{s.fileSize} Ko</span>
                    {s.positionName && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{s.positionName}</span>
                    )}
                  </div>
                </div>
                {/* Icône ouvrir */}
                <div className="flex-shrink-0">
                  {opening === s.id ? (
                    <div className="w-8 h-8 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                  ) : (
                    <ExternalLink size={18} className="text-blue-400" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </MobileLayout>
  )
}
